import React, { useState, useEffect } from 'react';
import { api } from './api';
import { formatDay, moscowDate } from './timetableUtils';
import './AdminUsers.css';
import './FileUpload.css';

const card = {
  background: 'var(--eu-surface)',
  padding: '20px',
  borderRadius: '12px',
  border: '1px solid var(--eu-border)',
  marginBottom: '24px',
};
const row = { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' };

// Основное расписание: видно ли оно учителям, до какой даты актуально
// и сколько длится урок (в выгрузке МЭШ есть только время начала).
// Скрытое или устаревшее расписание не удаляется — его видят только администраторы.
function LessonSettings() {
  const [info, setInfo] = useState(null);
  const [minutes, setMinutes] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setInfo((prev) => ({ ...prev, ...data }));
    setMinutes(String(data.lesson_minutes));
    setValidUntil(data.valid_until || '');
  };

  useEffect(() => {
    api.getTimetable().then(apply, () => {});
  }, []);

  if (!info) return null;

  const save = async (patch, okText = 'Сохранено') => {
    setSaving(true);
    setMessage(null);
    try {
      apply(await api.updateTimetableSettings(patch));
      setMessage({ ok: true, text: okText });
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const classes = new Set(info.lessons.map((l) => l.class_name)).size;
  const expired = info.valid_until && moscowDate() > info.valid_until;

  let status;
  if (info.hidden) {
    status = { tone: 'warn', text: 'Скрыто от учителей: расписание и блок «Сейчас» видят только администраторы.' };
  } else if (expired) {
    status = { tone: 'warn', text: `Срок актуальности истёк ${formatDay(info.valid_until)} — учителя расписание не видят. Загрузите новое или продлите дату.` };
  } else if (info.valid_until) {
    status = { tone: 'ok', text: `Учителя видят расписание до ${formatDay(info.valid_until)} включительно.` };
  } else {
    status = { tone: 'ok', text: 'Учителя видят расписание. Дата актуальности не указана — оно не скроется само.' };
  }

  return (
    <div style={card}>
      <h4 style={{ margin: '0 0 8px 0', color: 'var(--eu-text)' }}>Основное расписание: показ учителям</h4>
      <p style={{ margin: '0 0 12px 0', color: 'var(--eu-text-2)' }}>
        {info.lessons.length > 0
          ? `Разобрано: ${classes} классов, ${info.lessons.length} уроков` +
            (info.source_name ? ` · ${info.source_name}` : '')
          : 'Выгрузка МЭШ ещё не загружена — расписание показывается листами Excel.'}
      </p>

      <div style={{
        padding: '10px 14px',
        borderRadius: '10px',
        marginBottom: '14px',
        border: `1px solid ${status.tone === 'ok' ? 'var(--eu-success-border)' : '#ecc94b'}`,
        background: status.tone === 'ok' ? 'var(--eu-success-bg)' : 'rgba(236, 201, 75, 0.14)',
        color: status.tone === 'ok' ? 'var(--eu-success-text)' : 'var(--eu-text)',
      }}>
        {status.text}
      </div>

      <div style={{ display: 'grid', gap: '14px' }}>
        <label style={{ ...row, cursor: 'pointer', color: 'var(--eu-text)' }}>
          <input
            type="checkbox"
            checked={!info.hidden}
            disabled={saving}
            onChange={(e) => save(
              { hidden: !e.target.checked },
              e.target.checked ? 'Расписание снова видно учителям' : 'Расписание скрыто от учителей',
            )}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: 600 }}>Показывать расписание учителям</span>
        </label>

        <div style={row}>
          <label htmlFor="valid-until" style={{ color: 'var(--eu-text-2)' }}>Актуально до (включительно)</label>
          <input
            id="valid-until"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
          <button
            type="button"
            className="admin-button admin-button-save"
            onClick={() => save({ valid_until: validUntil || null })}
            disabled={saving || validUntil === (info.valid_until || '')}
          >
            Сохранить дату
          </button>
          {info.valid_until && (
            <button
              type="button"
              className="admin-button admin-button-cancel"
              onClick={() => save({ valid_until: null }, 'Дата снята')}
              disabled={saving}
            >
              Без срока
            </button>
          )}
        </div>

        <div style={row}>
          <label htmlFor="lesson-minutes" style={{ color: 'var(--eu-text-2)' }}>Длительность урока, минут</label>
          <input
            id="lesson-minutes"
            type="number"
            min="20"
            max="90"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            style={{ width: '80px' }}
          />
          <button
            type="button"
            className="admin-button admin-button-save"
            onClick={() => save({ lesson_minutes: Number(minutes) })}
            disabled={saving || minutes === String(info.lesson_minutes)}
          >
            Сохранить
          </button>
        </div>

        {message && (
          <span style={{ color: message.ok ? 'var(--eu-success-text)' : 'var(--eu-danger-text)' }}>{message.text}</span>
        )}
      </div>
    </div>
  );
}

export default function AdminSchedule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [uploadScheduleType, setUploadScheduleType] = useState('main'); // 'main' или 'extracurricular'
  // До какой даты загружаемое основное расписание актуально — обязательно
  const [uploadValidUntil, setUploadValidUntil] = useState('');
  // Перечитать настройки уроков после загрузки нового файла
  const [timetableKey, setTimetableKey] = useState(0);

  useEffect(() => {
    loadScheduleInfo();
  }, []);

  const loadScheduleInfo = async () => {
    try {
      // Загружаем информацию об основном расписании
      const mainData = await api.getScheduleSheets('main');
      // Загружаем информацию о внеурочном расписании
      const extracurricularData = await api.getScheduleSheets('extracurricular');
      
      setScheduleInfo({
        main: {
          hasFile: mainData.has_file,
          sheetsCount: mainData.sheets?.length || 0,
          sheets: mainData.sheets || [],
        },
        extracurricular: {
          hasFile: extracurricularData.has_file,
          sheetsCount: extracurricularData.sheets?.length || 0,
          sheets: extracurricularData.sheets || [],
        },
      });
    } catch (err) {
      console.error('Failed to load schedule info:', err);
      setScheduleInfo({
        main: {
          hasFile: false,
          sheetsCount: 0,
          sheets: [],
        },
        extracurricular: {
          hasFile: false,
          sheetsCount: 0,
          sheets: [],
        },
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Проверяем расширение файла (только Excel)
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setError('Пожалуйста, выберите файл Excel (.xlsx или .xls)');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Пожалуйста, выберите файл для загрузки');
      return;
    }
    if (uploadScheduleType === 'main' && !uploadValidUntil) {
      setError('Укажите, до какой даты расписание актуально');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await api.uploadSchedule(
        selectedFile,
        uploadScheduleType,
        false,
        uploadScheduleType === 'main' ? uploadValidUntil : '',
      );
      
      setSuccess(
        result.timetable
          // Выгрузка МЭШ разобрана в уроки — расписание и блок «Сейчас» обновятся сами
          ? `Расписание из МЭШ загружено: ${result.timetable.classes} классов, ` +
            `${result.timetable.lessons} уроков, актуально до ${formatDay(uploadValidUntil)}. ` +
            `Файл: ${result.file_name || selectedFile.name}.`
          : `Расписание успешно загружено! ` +
            `Файл: ${result.file_name || selectedFile.name}. ` +
            `Листов: ${result.sheets?.length || 0}`
      );
      setTimetableKey((k) => k + 1);
      
      // Очищаем форму
      setSelectedFile(null);
      setUploadValidUntil('');
      document.querySelector('input[type="file"]').value = '';
      
      // Обновляем информацию о расписании
      await loadScheduleInfo();
    } catch (err) {
      setError(err.message || 'Ошибка при загрузке расписания');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSchedule = async (scheduleTypeToDelete) => {
    const scheduleTypeName = scheduleTypeToDelete === 'extracurricular' ? 'внеурочных занятий' : 'основного расписания';
    if (!window.confirm(`Вы уверены, что хотите удалить файл ${scheduleTypeName}? Это действие нельзя отменить.`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Используем правильный API URL
      const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' 
        ? `${window.location.protocol}//${window.location.hostname}:8000/api`
        : 'http://127.0.0.1:8000/api');
      
      // Удаляем файл расписания через API с указанием типа
      const response = await fetch(`${API_BASE_URL}/schedule/delete-file/?schedule_type=${scheduleTypeToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Ошибка при удалении файла');
      }

      setSuccess(`Файл ${scheduleTypeName} успешно удален.`);
      await loadScheduleInfo();
    } catch (err) {
      setError(err.message || 'Ошибка при удалении файла расписания');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-users">
      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {success && (
        <div className="admin-success" style={{
          padding: '16px',
          background: 'var(--eu-success-bg)',
          border: '1px solid var(--eu-success-border)',
          borderRadius: '8px',
          color: 'var(--eu-success-text)',
          marginBottom: '24px',
        }}>
          {success}
        </div>
      )}

      <LessonSettings key={timetableKey} />

      {scheduleInfo && (
        <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div className="schedule-info-card" style={{
            background: 'var(--eu-surface)',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid var(--eu-border)',
            flex: '1',
            minWidth: '300px',
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: 'var(--eu-text)' }}>Основное расписание</h4>
            {scheduleInfo.main.hasFile ? (
              <>
                <p style={{ margin: '0 0 8px 0', color: 'var(--eu-text-2)' }}>
                  Загружено листов: <strong>{scheduleInfo.main.sheetsCount}</strong>
                </p>
                {scheduleInfo.main.sheets.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <p style={{ margin: '0 0 8px 0', color: 'var(--eu-text-2)', fontSize: '14px' }}>Листы:</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {scheduleInfo.main.sheets.map((sheetName, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '6px 12px',
                            background: 'var(--eu-recessed)',
                            border: '1px solid var(--eu-border)',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: 'var(--eu-text)',
                          }}
                        >
                          {sheetName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p style={{ margin: '0', color: 'var(--eu-text-muted)' }}>
                Расписание не загружено
              </p>
            )}
          </div>

          <div className="schedule-info-card" style={{
            background: 'var(--eu-surface)',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid var(--eu-border)',
            flex: '1',
            minWidth: '300px',
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: 'var(--eu-text)' }}>Внеурочные занятия</h4>
            {scheduleInfo.extracurricular.hasFile ? (
              <>
                <p style={{ margin: '0 0 8px 0', color: 'var(--eu-text-2)' }}>
                  Загружено листов: <strong>{scheduleInfo.extracurricular.sheetsCount}</strong>
                </p>
                {scheduleInfo.extracurricular.sheets.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <p style={{ margin: '0 0 8px 0', color: 'var(--eu-text-2)', fontSize: '14px' }}>Листы:</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {scheduleInfo.extracurricular.sheets.map((sheetName, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '6px 12px',
                            background: 'var(--eu-recessed)',
                            border: '1px solid var(--eu-border)',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: 'var(--eu-text)',
                          }}
                        >
                          {sheetName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p style={{ margin: '0', color: 'var(--eu-text-muted)' }}>
                Расписание не загружено
              </p>
            )}
          </div>
        </div>
      )}

      <div className="admin-form-container" style={{
        background: 'var(--eu-surface)',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid var(--eu-border)',
      }}>
        <h4 style={{ margin: '0 0 20px 0', color: 'var(--eu-text)' }}>Загрузить расписание из Excel</h4>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--eu-text)' }}>
              Тип расписания
            </label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="schedule_type"
                  value="main"
                  checked={uploadScheduleType === 'main'}
                  onChange={(e) => setUploadScheduleType(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Основное расписание</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="schedule_type"
                  value="extracurricular"
                  checked={uploadScheduleType === 'extracurricular'}
                  onChange={(e) => setUploadScheduleType(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Внеурочные занятия</span>
              </label>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--eu-text)' }}>
              Файл расписания (Excel)
            </label>
            <div className="file-upload-wrapper">
              <input
                type="file"
                id="file-upload-schedule"
                className="file-upload-input"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={loading}
              />
              <label htmlFor="file-upload-schedule" className={`file-upload-button ${loading ? 'disabled' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span>{selectedFile ? 'Изменить файл' : 'Выбрать файл Excel'}</span>
              </label>
            </div>
            {selectedFile && (
              <div className="file-upload-info">
                Выбран файл: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
              </div>
            )}
            <p className="file-upload-hint">
              Поддерживаются файлы в формате Excel (.xlsx или .xls). Каждый лист будет отображаться как отдельная таблица.
            </p>
          </div>

          {uploadScheduleType === 'main' && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label
                htmlFor="upload-valid-until"
                style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--eu-text)' }}
              >
                Актуально до (включительно)
              </label>
              <input
                id="upload-valid-until"
                type="date"
                required
                min={moscowDate()}
                value={uploadValidUntil}
                onChange={(e) => setUploadValidUntil(e.target.value)}
                disabled={loading}
              />
              <p className="file-upload-hint">
                На следующий день после этой даты расписание и блок «Сейчас» перестанут показываться учителям,
                чтобы не было устаревшей информации. Файл при этом не удаляется.
              </p>
            </div>
          )}

          <div className="form-actions" style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              className="admin-button admin-button-save"
              disabled={loading || !selectedFile || (uploadScheduleType === 'main' && !uploadValidUntil)}
              style={{
                opacity: loading || !selectedFile || (uploadScheduleType === 'main' && !uploadValidUntil) ? 0.6 : 1,
                cursor: loading || !selectedFile || (uploadScheduleType === 'main' && !uploadValidUntil) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <>
                  <span className="button-spinner"></span>
                  Загрузка...
                </>
              ) : (
                'Загрузить расписание'
              )}
            </button>
            
            {scheduleInfo && (scheduleInfo.main.hasFile || scheduleInfo.extracurricular.hasFile) && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {scheduleInfo.main.hasFile && (
                  <button
                    type="button"
                    className="admin-button admin-button-cancel"
                    onClick={() => handleClearSchedule('main')}
                    disabled={loading}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                    }}
                  >
                    Удалить основное расписание
                  </button>
                )}
                {scheduleInfo.extracurricular.hasFile && (
                  <button
                    type="button"
                    className="admin-button admin-button-cancel"
                    onClick={() => handleClearSchedule('extracurricular')}
                    disabled={loading}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                    }}
                  >
                    Удалить внеурочные занятия
                  </button>
                )}
              </div>
            )}
          </div>
        </form>
      </div>

      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'var(--eu-surface)',
        borderRadius: '8px',
        border: '1px solid var(--eu-border)',
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: 'var(--eu-text)' }}>Инструкция</h4>
        <ul style={{ margin: '0', paddingLeft: '20px', color: 'var(--eu-text-2)', fontSize: '14px', lineHeight: '1.6' }}>
          <li>Файл должен быть в формате Excel (.xlsx или .xls)</li>
          <li>Каждый лист в Excel файле будет отображаться как отдельная таблица на странице "Расписание"</li>
          <li>Названия листов будут использоваться как кнопки для переключения между таблицами</li>
          <li>Файл сохраняется на сервере и отображается всем пользователям в том виде, в котором он загружен</li>
        </ul>
      </div>
    </div>
  );
}

